import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Shield,
  UserCheck,
  Car,
  CheckCircle,
  Loader2,
  AlertTriangle,
  ArrowRight,
  Check,
  Sparkles,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import TripSummary from "@/components/TripSummary";
import RouteMap from "@/components/RouteMap";
import { useVehiclePricing } from "@/hooks/useVehiclePricing";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useSeo } from "@/hooks/useSeo";

const includedServices = [
  { icon: Shield, label: "Free Cancellation" },
  { icon: UserCheck, label: "Meet & Greet" },
  { icon: Car, label: "Private Transfer" },
  { icon: CheckCircle, label: "Licensed Chauffeurs" },
];

interface SelectedVehicle {
  id: string;
  name: string;
  image_url: string | null;
  price: number;
  capacity: number;
}

const VehicleSelection = () => {
  useSeo({ robots: "noindex,follow", title: "Choose your ride | Allen Travels RD" });
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedVehicle, setSelectedVehicle] = useState<SelectedVehicle | null>(null);

  const state = location.state as {
    from: string;
    to: string;
    fromPlaceId?: string;
    toPlaceId?: string;
    routeType: string;
    passengers: number;
    promoCode: string;
  } | null;

  const from = state?.from || "Punta Cana Airport (PUJ)";
  const to = state?.to || "Hard Rock Hotel Punta Cana";
  const routeType = state?.routeType || "one-way";
  const passengers = state?.passengers || 2;

  useEffect(() => {
    const hasValidPlaces =
      !!state?.fromPlaceId &&
      !!state?.toPlaceId &&
      (state.fromPlaceId.startsWith("db-hotel-") || state.fromPlaceId.startsWith("db-airport-")) &&
      (state.toPlaceId.startsWith("db-hotel-") || state.toPlaceId.startsWith("db-airport-"));

    if (!hasValidPlaces) {
      toast.error("Please select pick-up and drop-off from the suggestions list.", {
        description: "We need a registered hotel or airport to quote the right price.",
      });
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { serviceGroups, loading, distanceKm, durationMin, noServiceAvailable } = useVehiclePricing(
    from,
    to,
    state?.fromPlaceId,
    state?.toPlaceId,
    routeType,
    passengers
  );

  const handleContinue = () => {
    if (!selectedVehicle) return;
    navigate("/booking", {
      state: {
        from,
        to,
        fromPlaceId: state?.fromPlaceId,
        toPlaceId: state?.toPlaceId,
        routeType,
        passengers,
        promoCode: state?.promoCode,
        distanceKm,
        durationMin,
        vehicle: selectedVehicle,
      },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-soft flex flex-col">
      <Navbar />

      {/* Hero strip */}
      <section className="relative pt-28 pb-10 overflow-hidden isolate">
        <div className="absolute inset-0 z-0 bg-gradient-hero" />
        <div className="absolute inset-0 z-0 opacity-30 mix-blend-overlay bg-[radial-gradient(ellipse_at_top,white,transparent_60%)]" />
        <div className="relative z-10 container mx-auto px-4 text-white">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors mb-5"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-dark text-xs font-medium tracking-[0.22em] uppercase">
            <Sparkles className="w-3.5 h-3.5 text-accent" /> Step 2 of 3
          </span>
          <h1 className="font-display text-3xl md:text-5xl font-semibold mt-4 leading-tight">
            Choose your <span className="text-gradient-sunset">ride</span>
          </h1>
          <p className="mt-3 text-white/75 max-w-xl">
            Hand-picked vehicles, vetted chauffeurs, and a private experience door to door.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 pb-32 -mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT: Vehicles */}
          <div className="lg:col-span-2 space-y-8">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 gap-4 glass rounded-2xl shadow-card">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                <p className="text-muted-foreground">Finding the best vehicles for your route…</p>
              </div>
            ) : noServiceAvailable ? (
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center justify-center py-20 gap-6 text-center glass rounded-2xl shadow-card p-10"
              >
                <div className="w-14 h-14 rounded-2xl bg-accent/15 flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-accent" />
                </div>
                <div>
                  <h2 className="font-display text-2xl font-semibold text-foreground mb-2">
                    No service available
                  </h2>
                  <p className="text-muted-foreground max-w-md">
                    We don't currently cover this exact route. Try a different combination or contact us.
                  </p>
                </div>
                <Button onClick={() => navigate("/")} className="bg-gradient-ocean shadow-glow">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Search
                </Button>
              </motion.div>
            ) : serviceGroups.length === 0 ? (
              <div className="text-center py-20 glass rounded-2xl shadow-card">
                <p className="text-muted-foreground">
                  No vehicles available for {passengers} passenger{passengers !== 1 ? "s" : ""} on this route.
                </p>
              </div>
            ) : (
              serviceGroups.map((group) => (
                <div key={group.id} className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-border" />
                    <h2 className="font-display text-sm font-semibold text-primary uppercase tracking-[0.22em]">
                      {group.name}
                    </h2>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  {group.vehicles.map((vehicle, index) => {
                    const isSelected = selectedVehicle?.id === vehicle.id;
                    return (
                      <motion.div
                        key={vehicle.id}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        onClick={() =>
                          setSelectedVehicle({
                            id: vehicle.id,
                            name: vehicle.name,
                            image_url: vehicle.image_url,
                            price: vehicle.price,
                            capacity: vehicle.capacity,
                          })
                        }
                        className={`relative flex flex-col md:flex-row gap-6 p-6 rounded-2xl bg-card border transition-all cursor-pointer group overflow-hidden ${
                          isSelected
                            ? "border-primary ring-2 ring-primary/30 shadow-elegant"
                            : "border-border/60 hover:border-primary/40 shadow-soft hover:shadow-card"
                        }`}
                      >
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gradient-ocean shadow-glow flex items-center justify-center z-10"
                          >
                            <Check className="w-4 h-4 text-white" />
                          </motion.div>
                        )}

                        <div className="relative w-full md:w-60 aspect-[4/3] md:aspect-auto md:h-44 rounded-xl overflow-hidden shrink-0">
                          <img
                            src={vehicle.image_url || "/placeholder.svg"}
                            alt={vehicle.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            loading="lazy"
                          />
                          {vehicle.badge && (
                            <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-gradient-sunset text-white shadow-glow">
                              {vehicle.badge}
                            </span>
                          )}
                        </div>

                        <div className="flex-1 flex flex-col justify-between">
                          <div>
                            <h3 className="font-display text-2xl font-semibold text-foreground">
                              {vehicle.name}
                            </h3>
                            <p className="text-muted-foreground text-sm mt-1 flex items-center gap-1.5">
                              <Users className="w-3.5 h-3.5" />
                              {vehicle.min_passengers}–{vehicle.capacity} passengers
                            </p>
                            {(vehicle as any).description && (
                              <p className="text-muted-foreground text-sm mt-2">{(vehicle as any).description}</p>
                            )}
                            {vehicle.features && vehicle.features.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-3">
                                {vehicle.features.map((f) => (
                                  <Badge key={f} variant="secondary" className="text-[11px] font-normal">
                                    {f}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-end justify-between mt-5 pt-4 border-t border-border/60">
                            <div>
                              <p className="text-3xl font-display font-semibold text-gradient-ocean leading-none">
                                ${vehicle.price}
                              </p>
                              <p className="text-muted-foreground text-xs mt-1">
                                {routeType === "round-trip" ? "round trip" : "one way"}
                              </p>
                            </div>
                            <Button
                              variant={isSelected ? "default" : "outline"}
                              className={isSelected ? "bg-gradient-ocean shadow-glow" : ""}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedVehicle({
                                  id: vehicle.id,
                                  name: vehicle.name,
                                  image_url: vehicle.image_url,
                                  price: vehicle.price,
                                  capacity: vehicle.capacity,
                                });
                              }}
                            >
                              {isSelected ? (
                                <>
                                  <Check className="w-4 h-4 mr-1" /> Selected
                                </>
                              ) : (
                                "Select"
                              )}
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* RIGHT: Trip summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="sticky top-28 p-6 rounded-2xl glass shadow-elegant space-y-5"
            >
              <h3 className="font-display text-lg font-semibold text-foreground">Trip Summary</h3>

              <RouteMap from={from} to={to} />

              <TripSummary
                from={from}
                to={to}
                distanceKm={distanceKm}
                durationMin={durationMin}
                passengers={passengers}
                loadingRoute={loading}
                routeType={routeType}
              />

              <div className="border-t border-border/60 pt-5">
                <p className="text-[11px] text-muted-foreground uppercase tracking-[0.18em] mb-3 font-semibold">
                  Included in every transfer
                </p>
                <div className="space-y-2.5">
                  {includedServices.map((s) => (
                    <div key={s.label} className="flex items-center gap-2.5 text-sm">
                      <s.icon className="w-4 h-4 text-primary" />
                      <span className="text-foreground">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom sticky bar */}
      <AnimatePresence>
        {selectedVehicle && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-lg border-t border-border shadow-elegant"
          >
            <div className="container mx-auto px-4 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <div className="hidden sm:block w-14 h-14 rounded-xl overflow-hidden shrink-0 border border-border">
                  <img
                    src={selectedVehicle.image_url || "/placeholder.svg"}
                    alt={selectedVehicle.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <p className="font-display font-semibold text-foreground truncate">{selectedVehicle.name}</p>
                  <p className="font-display font-semibold text-lg text-gradient-ocean leading-tight">
                    ${selectedVehicle.price}{" "}
                    <span className="text-muted-foreground text-xs font-sans font-normal">
                      {routeType === "round-trip" ? "round trip" : "one way"}
                    </span>
                  </p>
                </div>
              </div>
              <Button
                size="lg"
                className="font-semibold shrink-0 bg-gradient-ocean shadow-glow h-12"
                onClick={handleContinue}
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <Footer />
    </div>
  );
};

export default VehicleSelection;
